// engine-exception: the billing run groups unbilled T&M time by client and turns a multi-row selection into one draft invoice per client via POST /invoices/from-time — a grouped multi-select-to-workflow with no backing resource, which no ScreenDef kind expresses
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  EmptyState,
  ErrorBanner,
  Money,
  PageHeader,
  Skeleton,
  Stat,
  useAction,
  useApiQuery,
  useInvalidateResource,
  useUpdate,
} from '../../engine';
import { DataGrid, type GridColumn } from '../../engine/views/DataGrid';
import { formatDate, formatMinutes, sumMinor } from '../../lib/format';
import { field } from './lib';

// The billing run: every billable T&M entry not yet invoiced. Grouped by client in one
// engine DataGrid; a selection → one draft invoice per client (fn_invoice_billable_time
// snapshots each entry onto its own line and links it, so nothing double-bills). Orphaned
// time gets a client assigned inline. With the review gate on, only closed-period time bills.

interface Unbilled {
  id: string;
  billing_party_id: string | null;
  client_id: string | null;
  client_name: string | null;
  technician_name: string | null;
  occurred_on: string;
  note: string | null;
  minutes: number;
  bill_rate_minor: string;
  billable_amount_minor: string;
  currency: string;
  timesheet_status: string | null;
}

const CLIENT_FIELD = field({ name: 'client_id', type: 'relation', relation: { resource: 'clients', displayField: 'name' } });

export function BillingRunScreen() {
  const navigate = useNavigate();
  const unbilledQ = useApiQuery<{ data: Unbilled[] }>(['unbilled-time'], '/v1/time-entries/unbilled');
  const settingsQ = useApiQuery<{ require_locked_time_to_invoice?: boolean }>(['settings'], '/v1/settings');
  const assign = useUpdate('time-entries', { toast: { success: 'Client assigned' }, invalidate: ['invoices'] });
  const generate = useAction<{ id: string }>('invoices', 'from-time', { toast: { success: false } });
  const invalidate = useInvalidateResource();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const gate = settingsQ.data?.require_locked_time_to_invoice ?? false;
  const invoiceable = (e: Unbilled) => !gate || e.timesheet_status === 'locked';
  const rows = useMemo(() => unbilledQ.data?.data ?? [], [unbilledQ.data]);

  const groups = useMemo(() => new Set(rows.filter((r) => r.billing_party_id).map((r) => r.billing_party_id)).size, [rows]);
  const totalMinor = sumMinor(rows, (e) => e.billable_amount_minor);
  const totalMinutes = rows.reduce((s, e) => s + e.minutes, 0);
  const headCurrency = rows[0]?.currency ?? 'USD';

  const refresh = () => {
    void unbilledQ.refetch();
    invalidate('invoices');
  };

  const invoiceSelected = async (ids: string[]) => {
    const chosen = rows.filter((r) => ids.includes(r.id) && r.billing_party_id && invoiceable(r));
    const byParty = new Map<string, string[]>();
    for (const e of chosen) {
      const arr = byParty.get(e.billing_party_id!) ?? [];
      arr.push(e.id);
      byParty.set(e.billing_party_id!, arr);
    }
    if (byParty.size === 0) return;
    let firstId: string | null = null;
    for (const [party, entryIds] of byParty) {
      const inv = await generate.mutateAsync({ body: { billing_party_id: party, time_entry_ids: entryIds } });
      firstId = firstId ?? inv.id;
    }
    setSelected(new Set());
    refresh();
    if (firstId) navigate(`/invoices/${firstId}`);
  };

  const columns: GridColumn<Unbilled>[] = [
    { id: 'client_name', header: 'Client', field: { ...CLIENT_FIELD, inUpdate: true }, editable: true, cell: (e) => (e.billing_party_id ? (e.client_name ?? '—') : <span className="hf-muted">Assign a client…</span>) },
    { id: 'occurred_on', header: 'Date', width: 120, cell: (e) => <span className="hf-num">{formatDate(e.occurred_on)}</span> },
    { id: 'technician_name', header: 'Technician', cell: (e) => e.technician_name ?? '—' },
    { id: 'note', header: 'Note', cell: (e) => <span className="hf-muted">{e.note ?? '—'}</span> },
    { id: 'minutes', header: 'Time', align: 'right', cell: (e) => <span className="hf-num">{formatMinutes(e.minutes)}</span> },
    { id: 'bill_rate_minor', header: 'Rate', align: 'right', cell: (e) => <span className="hf-num"><Money minor={e.bill_rate_minor} currency={e.currency} />/h</span> },
    { id: 'billable_amount_minor', header: 'Amount', align: 'right', cell: (e) => (invoiceable(e) ? <Money minor={e.billable_amount_minor} currency={e.currency} /> : <Badge tone="warn" dot={false}>not reviewed</Badge>) },
  ];

  return (
    <div className="hf-page">
      <PageHeader
        title="Billing run"
        subtitle={gate ? 'Only time from closed periods can be invoiced (workspace setting) — close a timesheet to release its time.' : 'Billable time waiting to become invoices — select rows and invoice, per client.'}
      />
      <div className="hf-stats" aria-label="Billing run summary">
        <Stat label="Unbilled value" accent value={<Money minor={totalMinor} currency={headCurrency} />} sub={`${formatMinutes(totalMinutes)} across ${rows.length} ${rows.length === 1 ? 'entry' : 'entries'}`} icon="receipt" />
        <Stat label="Clients waiting" value={<span className="hf-num">{groups}</span>} sub="each generates its own invoice" icon="building" />
      </div>
      {unbilledQ.isLoading ? (
        <Skeleton lines={6} />
      ) : unbilledQ.error ? (
        <ErrorBanner error={unbilledQ.error} />
      ) : (
        <DataGrid<Unbilled>
          aria-label="Unbilled time"
          columns={columns}
          rows={rows}
          keyOf={(e) => e.id}
          groupBy="client_name"
          selection={selected}
          onSelectionChange={setSelected}
          onCellEdit={(e, f, value) => assign.mutateAsync({ id: e.id, body: { [f.name]: value } }).then(refresh)}
          bulkActions={[{ name: 'invoice', label: 'Invoice selected clients', icon: 'receipt', tone: 'primary', run: invoiceSelected }]}
          empty={<EmptyState title="Nothing to bill" hint="Billable T&M time appears here as soon as it is logged — invoice it before it goes stale." icon="check-circle" />}
        />
      )}
    </div>
  );
}
